// src/pages/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { auth as firebaseAuth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import QRCode from "react-qr-code";
import logo from "../assets/logo.png";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  getAuth,
} from "firebase/auth";
import LoadingSpinner from "../components/LoadingSpinner";
import { useAlert } from "../components/CustomAlert";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import AuthNav from "../components/AuthNav";

// 统一计算会员当前状态：已开通 / 已过期 / 已停用
function getMembershipStatus(membership) {
  if (!membership) {
    return { text: "未开通 inactive", color: "#999999" };
  }

  // active 可能不存在，默认当 true
  const isActive = typeof membership.active === "boolean"
    ? membership.active
    : true;

  // 计算是否过期
  let isExpired = false;
  try {
    if (membership.expiresAt) {
      let expiresDate;
      if (typeof membership.expiresAt.toDate === "function") {
        expiresDate = membership.expiresAt.toDate();
      } else {
        // 兜底：如果是普通日期字符串/时间戳
        expiresDate = new Date(membership.expiresAt);
      }
      isExpired = expiresDate.getTime() < Date.now();
    }
  } catch (e) {
    isExpired = false;
  }

  if (!isActive) {
    return { text: "已停用 cancelled", color: "#999999" };
  }

  if (isExpired) {
    return { text: "已过期 expired", color: "#999999" };
  }

  return { text: "已开通 active", color: "#11B65F" };
}

function Dashboard({ loading, handleLogout, switchLanguage, i18n }) {
  // ✅ 分开取不同 namespace
  const { t: tDashboard } = useTranslation("dashboard");
  const { t: tCoupons } = useTranslation("coupons");
  const { t: tPayment } = useTranslation("payment");
  const { t: tActions } = useTranslation("actions");
  const { t: tVerify } = useTranslation("verify");
  const { t: tCommon } = useTranslation("common");

  const { showAlert } = useAlert();
  const [userData, setUserData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefreshing, setAutoRefreshing] = useState(false);

  // ✅ 当前是否有有效会员（后面统一用这个来控制按钮显示）
  const hasActiveMembership = !!(userData && userData.active);

  // ✅ 语言切换：优先用父组件传进来的 switchLanguage，没有就直接用 i18n
  const changeLang = function (lang) {
    // 只接受 zh / en，其他一律按 zh 处理
    var finalLang = lang === "en" ? "en" : "zh";

    // 1) 调用 i18n 切换
    if (i18n && typeof i18n.changeLanguage === "function") {
      i18n.changeLanguage(finalLang);
    } else if (typeof switchLanguage === "function") {
      switchLanguage(finalLang);
    }

    // 2) 记在 localStorage，保证刷新/回跳后还能恢复
    try {
      localStorage.setItem("selectedLang", finalLang);
    } catch (e) {
      console.warn("save lang to localStorage failed", e);
    }

    // 3) 把 ?lang=xx 写回 URL，方便 Stripe Return 用
    try {
      var url = new URL(window.location.href);
      url.searchParams.set("lang", finalLang);
      window.history.replaceState({}, document.title, url.toString());
    } catch (e) {
      console.warn("update lang in url failed", e);
    }
  };

  // ✅ 小工具：防止出现 dashboard.xxx 原样显示
  const safeText = function (tFunc, key, fallbackText) {
    var v = tFunc(key);
    if (!v) {
      return fallbackText;
    }
    if (v === key) {
      return fallbackText;
    }
    return v;
  };

  // 调试代码：使用当前 auth 实例
  const auth = getAuth();
  console.log("当前用户对象：", auth.currentUser);
  if (auth.currentUser) {
    console.log("当前 UID:", auth.currentUser.uid);
  } else {
    console.log("⚠️ 没有登录用户");
  }

  // === 语言相关 useEffect（保持你现有逻辑，不在这里动） ===
  useEffect(() => {
    const savedLang = localStorage.getItem("selectedLang");
    if (savedLang && i18n.language !== savedLang) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  // 监听语言变化强制刷新组件渲染；从URL读取 lang 参数，回跳后恢复 Stripe 前的语言
  useEffect(() => {
    if (!i18n) return;

    try {
      const search = window.location.search || "";
      if (!search) return;

      const params = new URLSearchParams(search);
      const urlLang = params.get("lang");
      if (!urlLang) return;

      // 只接受 zh / en，避免乱值
      let normalized = null;
      if (urlLang === "zh" || urlLang === "en") {
        normalized = urlLang;
      }

      if (!normalized) return;

      if (normalized !== i18n.language) {
        changeLang(normalized); // 用现有的 changeLang 包装函数
      }
    } catch (e) {
      console.warn("Dashboard: parse lang from URL failed", e);
    }
  }, [i18n]);

  // ✅ 自动登录
  useEffect(function () {
    if (!auth.currentUser) {
      var email = localStorage.getItem("riceroma_email");
      var password = localStorage.getItem("riceroma_password");

      if (email && password) {
        signInWithEmailAndPassword(auth, email, password)
          .then(function () {
            localStorage.removeItem("riceroma_email");
            localStorage.removeItem("riceroma_password");
            window.location.reload();
          })
          .catch(function () {
            localStorage.removeItem("riceroma_email");
            localStorage.removeItem("riceroma_password");
          });
      }
    }
  }, [auth]);

  // ✅ 支付成功后提示
  useEffect(
    function () {
      var urlParams = new URLSearchParams(window.location.search);
      var isSuccess = urlParams.get("success") === "true";

      if (isSuccess) {
        var checkAndNotify = setInterval(function () {
          if (auth.currentUser && userData && userData.active) {
            showAlert(
              safeText(tCommon, "successTitle", "成功"),
              safeText(tPayment, "success", "支付成功，欢迎加入会员！"),
              safeText(tActions, "ok", "我知道了"),
              "success"
            );

            // 清除本地缓存
            localStorage.removeItem("riceroma_email");
            localStorage.removeItem("riceroma_password");

            // 清掉 ?success=true
            var cleanUrl =
              window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);

            clearInterval(checkAndNotify);
          }
        }, 500);

        // 兜底 10s
        setTimeout(function () {
          clearInterval(checkAndNotify);
        }, 10000);
      }
    },
    [auth, userData, showAlert, tCommon, tPayment, tActions]
  );

  // ✅ 监听登录状态并取 Firestore 会员信息
  useEffect(function () {
    var unsubscribe = onAuthStateChanged(auth, async function (user) {
      if (!user) {
        console.log("Dashboard: no user, skip Firestore.");
        setUserData(null);
        setDataLoading(false);
        return;
      }

      if (!user.email) {
        console.log("Dashboard: user has no email, skip Firestore.");
        setUserData(null);
        setDataLoading(false);
        return;
      }

      // 强制验证过邮箱才看 dashboard
      if (!user.emailVerified) {
        console.log("Dashboard: email not verified, skip Firestore.");
        setUserData(null);
        setDataLoading(false);
        return;
      }

      try {
        console.log("Dashboard: loading membership for uid =", user.uid);
        const docRef = doc(db, "memberships", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("Dashboard: membership doc =", data);

          if (data.active) {
            setUserData(data);
          } else {
            console.log("Dashboard: membership not active.");
            setUserData(null);
          }
        } else {
          console.log("Dashboard: membership doc not found.");
          setUserData(null);
        }
      } catch (error) {
        console.error("获取会员数据失败:", error);
        setUserData(null);
      } finally {
        setDataLoading(false);
        setLastUpdated(new Date()); 
      }
    });

    return function () {
      unsubscribe();
    };
  }, [auth]);

  // ✅ 管理会员 (Stripe Billing Portal)
  const handleManageMembership = async function () {
    const user = auth.currentUser;
    if (!user || !user.email) {
      showAlert(
        safeText(tCommon, "errorDefault", "出现问题，请稍后再试。"),
        "请先登录后再管理会员。",
        safeText(tActions, "ok", "我知道了"),
        "error"
      );
      return;
    }

    try {
      // 带上 uid、email 和 returnUrl
      const res = await fetch(
        "https://createbillingportalsession-7boqzcg2xq-uc.a.run.app",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: user.uid,
            email: user.email,
            returnUrl:
              window.location.origin +
              "/dashboard?lang=" +
              ((i18n && i18n.language) || "zh"),
          }),
        }
      );

      const data = await res.json();

      // 成功：后端返回 url，直接跳过去
      if (res.ok && data && data.url) {
        window.location.href = data.url;
        return;
      }

      // 失败：后端给了 message 就展示，没有就用兜底文案
      const msg =
        (data && data.message) ||
        safeText(
          tCommon,
          "errorDefault",
          "暂时无法打开会员管理页面，请稍后再试。"
        );

      showAlert(
        safeText(tCommon, "errorDefault", "出现问题，请稍后再试。"),
        msg,
        safeText(tActions, "ok", "我知道了"),
        "error"
      );
    } catch (err) {
      console.error("handleManageMembership error:", err);
      showAlert(
        safeText(tCommon, "errorDefault", "出现问题，请稍后再试。"),
        safeText(
          tCommon,
          "errorDefault",
          "暂时无法打开会员管理页面，请稍后再试。"
        ),
        safeText(tActions, "ok", "我知道了"),
        "error"
      );
    }
  };

  // ✅ 订阅按钮逻辑：增加「已有会员时禁止重复开通」判断
  const handleSubscribe = async function (membershipType) {
    var user = auth.currentUser;
    if (!user || !user.email) {
      showAlert(
        safeText(tCommon, "errorDefault", "出现问题，请稍后再试。"),
        safeText(tVerify, "invalidLink", "请先登录再订阅会员！"),
        safeText(tActions, "ok", "我知道了"),
        "error"
      );
      return;
    }

    // ✅ 新增：如果已经有 active 会员，阻止重复开通 / 交给 Portal 管理
    if (hasActiveMembership && userData) {
      // 1) 同一种卡：直接提示“不需要重复开通”
      if (userData.membershipType === membershipType) {
        var sameMsg =
          membershipType === "monthly"
            ? safeText(
                tDashboard,
                "alreadyMonthly",
                "你已经是月卡会员，无需重复开通，请在下方福利中使用权益。"
              )
            : safeText(
                tDashboard,
                "alreadyYearly",
                "你已经是年卡会员，无需重复开通，请在下方福利中使用权益。"
              );

        showAlert(
          safeText(tCommon, "errorDefault", "提示"),
          sameMsg,
          safeText(tActions, "ok", "我知道了"),
          "info"
        );
        return;
      }

      // 2) 不同卡种：暂时不允许在这里切换，提示去「管理我的会员」
      var switchMsg = safeText(
        tDashboard,
        "switchPlanInPortal",
        "如需变更会员类型，请通过「管理我的会员」页面在 Stripe 中修改订阅。"
      );
      showAlert(
        safeText(tCommon, "errorDefault", "提示"),
        switchMsg,
        safeText(tActions, "ok", "我知道了"),
        "info"
      );
      return;
    }

    try {
      // 把当前邮箱存一下，支付回来自动登录
      localStorage.setItem("riceroma_email", user.email);
      localStorage.setItem(
        "riceroma_password",
        localStorage.getItem("riceroma_password")
      );

      var response = await fetch(
        "https://createcheckoutsession-7boqzcg2xq-uc.a.run.app",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            membershipType: membershipType,
            email: user.email,
            language: (i18n && i18n.language) || "zh",
          }),
        }
      );

      var data = await response.json();
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        showAlert(
          safeText(tCommon, "errorDefault", "出现问题，请稍后再试。"),
          safeText(tCommon, "errorDefault", "出现问题，请稍后再试。") +
            "：" +
            (data && data.error ? data.error : ""),
          safeText(tActions, "ok", "我知道了"),
          "error"
        );
      }
    } catch (error) {
      showAlert(
        safeText(tCommon, "errorDefault", "出现问题，请稍后再试。"),
        safeText(tCommon, "errorDefault", "出现问题，请稍后再试。"),
        safeText(tActions, "ok", "我知道了"),
        "error"
      );
    }
  };

  // 渲染会员福利
  const renderBenefits = function () {
  if (!userData || !userData.membershipType) {
    return null;
  }

  // 月卡
  if (userData.membershipType === "monthly") {
    return (
      <ul className="list">
        <li>
          {safeText(tDashboard, "benefitsMonthly0", 
            "月卡会员每月首次到店扫码，可以免费兑换1份招牌饭")}
        </li>
        <li>
          {safeText(
            tDashboard,
            "benefitsMonthly1",
            "月卡会员享受会员价"
          )}
        </li>
        <li>
          {safeText(
            tDashboard,
            "benefitsMonthly2",
            "月卡会员享受会员专属活动，如早餐套餐"
          )}
        </li>
      </ul>
    );
  }

  // 年卡
  if (userData.membershipType === "yearly") {
    return (
      <ul className="list">
        <li>
          {safeText(tDashboard, "benefitsYearly0", 
            "年卡会员每月首次到店扫码，可以免费兑换1份招牌饭（全年累计12份）")}
        </li>
        <li>
          {safeText(
            tDashboard,
            "benefitsYearly1",
            "年卡会员享受会员价"
          )}
        </li>
        <li>
          {safeText(
            tDashboard,
            "benefitsYearly2",
            "年卡会员享受会员专属活动，如早餐套餐"
          )}
        </li>
        <li>
          {safeText(
            tDashboard,
            "benefitsYearly3",
            "年卡会员享受会员费折上折，平均每月费用更低"
          )}
        </li>
      </ul>
    );
  }

  return null;
  };

  // ✅ 渲染会员券
  const renderCoupons = function () {
    return (
      <ul className="list">
        <li>{safeText(tCoupons, "redeemable", "可在门店兑换指定商品")}</li>
      </ul>
    );
  };

  const showCouponsSection = false; // 暂时不开启会员券

  const membershipStatus = getMembershipStatus(userData);
// 如果你那块是用 membership 这个 state，
// 就写：const membershipStatus = getMembershipStatus(membership);

  return (
    <div className="dashboard-background">
      {/* 顶部统一导航 */}
      <AuthNav active="member" />

      <motion.div
        className="auth-page-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.35 }}
      >
        {/* 顶部 语言切换 */}
        <div className="lang-toggle">
          <button
            type="button"
            className={
              "lang-btn" + (i18n && i18n.language === "zh" ? " active" : "")
            }
            onClick={function () {
              changeLang("zh");
            }}
          >
            中文
          </button>
          <span className="lang-divider">/</span>
          <button
            type="button"
            className={
              "lang-btn" + (i18n && i18n.language === "en" ? " active" : "")
            }
            onClick={function () {
              changeLang("en");
            }}
          >
            English
          </button>
        </div>

        {/* 欢迎语与会员编号 */}
        <div className="card">
          <h2>{safeText(tDashboard, "welcome", "欢迎回来")}</h2>
          {userData && userData.memberId ? (
            <p>
              {safeText(tDashboard, "membershipMemberId", "会员编号")}{" "}
              <span className="highlight">{userData.memberId}</span>
            </p>
          ) : null}
        </div>
        
        {/* 状态更新提示 / 上次更新时间 */}
        {!dataLoading && lastUpdated && (
          <p className="dashboard-tip">
            {safeText(
              tDashboard,
              "statusLastUpdated",
              "会员状态已更新至"
            )}{" "}
            {lastUpdated.toLocaleTimeString(i18n.language)}
          </p>
        )}

        {/* 会员信息 */}
        {dataLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="card">
            <h3>{safeText(tDashboard, "membershipTitle", "会员信息")}</h3>
            {userData ? (
              <>
                <p>
                  {safeText(tDashboard, "membershipLevel", "会员类型")}{" "}
                  <strong className="highlight">
                    {userData.membershipType === "monthly"
                      ? safeText(tDashboard, "membershipMonthly", "月卡会员")
                      : safeText(
                          tDashboard,
                          "membershipYearly",
                          "年卡会员"
                        )}
                  </strong>
                </p>
                <p>
                  {safeText(tDashboard, "membershipValidity", "有效期至")}{" "}
                  {userData.expiresAt
                    ? new Date(
                        userData.expiresAt.toDate
                          ? userData.expiresAt.toDate()
                          : userData.expiresAt
                      ).toLocaleDateString(i18n.language)
                    : safeText(
                        tDashboard,
                        "membershipUnknown",
                        "暂无数据"
                      )}
                </p>
                <p>
                  {safeText(tDashboard, "membershipStatus", "当前状态")}{" "}
                  <strong
                  className="highlight"
                  style={{ color: membershipStatus.color }}
                  >
                  {membershipStatus.text}
                  </strong>
                </p>
              </>
            ) : (
              <p>
                {safeText(
                  tDashboard,
                  "errorNodata",
                  "暂未查询到会员数据"
                )}
              </p>
            )}
          </div>
        )}

        {/* 二维码 */}
        {typeof window !== "undefined" && userData && userData.active ? (
          <div className="card" style={{ textAlign: "center" }}>
            <h3>{safeText(tDashboard, "qrcodeTitle", "会员二维码")}</h3>
            <a
              href={
                "https://riceroma202606.web.app/verify?memberId=" +
                userData.memberId
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <QRCode
                value={
                  "https://riceroma202606.web.app/verify?memberId=" +
                  userData.memberId
                }
                size={160}
                bgColor="#ffffff"
                fgColor="#000000"
                level="H"
              />
            </a>
            <p
              style={{ marginTop: "12px", fontSize: "14px", color: "#666" }}
            >
              {safeText(tDashboard, "qrcodeTip", "请在门店出示二维码")}
            </p>
          </div>
        ) : null}

        {/* ✅ 订阅 / 管理 按钮区域 */}
        <div className="button-group">
          {/* 没有 active 会员：显示月卡 + 年卡订阅按钮 */}
          {!hasActiveMembership && (
            <>
              <button
                className="button subscribe-button"
                onClick={function () {
                  handleSubscribe("monthly");
                }}
              >
                {safeText(
                  tDashboard,
                  "subscribeMonthly",
                  "开通月卡会员"
                )}
              </button>
              <button
                className="button subscribe-button"
                onClick={function () {
                  handleSubscribe("yearly");
                }}
              >
                {safeText(
                  tDashboard,
                  "subscribeYearly",
                  "开通年卡会员"
                )}
              </button>
            </>
          )}

          {/* 有 active 会员：只保留「管理我的会员」入口 */}
          {hasActiveMembership && (
            <button className="button" onClick={handleManageMembership}>
              {safeText(
                tDashboard,
                "manageMembership",
                "管理我的会员"
              )}
            </button>
          )}
        </div>

        {/* 会员福利 */}
        <div className="card">
          <h3>{safeText(tDashboard, "benefitsTitle", "会员福利")}</h3>
          {renderBenefits()}
        </div>

        {/* 会员券 */}
        {showCouponsSection && (
                <div className="card">
          <h3>{safeText(tDashboard, "couponsTitle", "会员券")}</h3>
          {renderCoupons()}
        </div>
        )}

        {/* 登出 */}
        <button
          onClick={handleLogout}
          className="button"
          style={{ marginTop: "20px" }}
        >
          {tActions("logout")}
        </button>
      </motion.div>
    </div>
  );
}

export default Dashboard;
